import api from '@/api';
import { object, ValidationError } from 'yup';
import { addressSchema } from '../schemas/v1/address';
import { yupObject, yupString } from './yup';
import { PropertyType } from '@/helpers/const';

const singeSecurityRequired = [PropertyType.RETIREMENT_LAND_OR_COMMUNITY_HOME, PropertyType.VACANT_LAND];
const postcodeCache: Record<string, boolean> = {};

export const yupPostcode = () => {
  return yupString()
    .required()
    .matches(/^\d{4}$/, 'Postcode must be 4 digits')
    .label('Postcode')
    .test('is-valid-postcode', 'Please enter a valid postcode', async (value) => {
      if (!value || value.length !== 4) return true;

      if (postcodeCache[value] !== undefined) {
        return postcodeCache[value];
      }

      try {
        const response = await api.backend.public.postCode({ postCode: value });
        const isValid = response.status === 200;
        postcodeCache[value] = isValid;
        return isValid;
      } catch (error) {
        postcodeCache[value] = false;
        return false;
      }
    });
};

export function yupAddress() {
  return object().test(
    'address',
    ({ label }) => `${label} is not valid`,
    async (value, ctx) => {
      try {
        if (value) {
          await addressSchema.validate(value, { abortEarly: false });
        }
        return true;
      } catch (err) {
        if (err instanceof ValidationError) {
          const errorResult = ctx.createError();
          errorResult.errors.push(...err.errors);
          return errorResult;
        }
        throw err;
      }
    },
  );
}

export function yupAddressStatePostcode() {
  return yupObject({
    addressLine: yupString(),
    addressLine2: yupString(),
    locality: yupString(),
    state: yupString().required().label('State'),
    postcode: yupPostcode(),
    countryCode: yupString().default('AU').label('Country Code'),
  });
}

export function yupAddressPostcode() {
  return yupObject({
    addressLine: yupString(),
    addressLine2: yupString(),
    locality: yupString(),
    state: yupString(),
    postcode: yupPostcode(),
    countryCode: yupString().default('AU').label('Country Code'),
  });
}

export function yupSingleSecurityAddress() {
  return yupObject({
    addressLine: yupString(),
    addressLine2: yupString(),
    locality: yupString(),
    state: yupString().label('State'),
    postcode: yupString().label('Postcode'),
    countryCode: yupString().default('AU').label('Country Code'),
  }).when('propertyType', ([propertyType], schema) => {
    const isSingleSecurity = singeSecurityRequired.includes(propertyType);

    if (!isSingleSecurity) {
      return schema.shape({ postcode: yupPostcode() }).required().label('Property address');
    }

    return schema.optional().nullable();
  });
}
